import { useState, useEffect, useCallback, useRef } from "react";
import UIkit from "uikit";
import feather from "feather-icons";
import CRMService from "../../../services/crm.service";

const FeatherIcon = ({ name, size = 16, className = "" }) => {
  const icon = feather.icons[name];
  if (!icon) return null;
  return (
    <svg
      className={`feather-icon ${className}`}
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      dangerouslySetInnerHTML={{ __html: icon.contents }}
    />
  );
};

const ENTITY_TABS = [
  { key: "contact", label: "Owners" },
  { key: "guest", label: "Guests" },
  { key: "unit", label: "Units" },
  { key: "lead", label: "Leads" },
  { key: "housekeeper", label: "Housekeepers" },
  { key: "maintenance", label: "Maintenance" },
  { key: "vendor", label: "Vendors" },
  { key: "other", label: "Other" },
];

const FIELD_TYPE_OPTIONS = [
  { value: "text", label: "Text" },
  { value: "textarea", label: "Textarea" },
  { value: "number", label: "Number" },
  { value: "date", label: "Date" },
  { value: "boolean", label: "Yes / No" },
  { value: "select", label: "Dropdown" },
];

const slugify = (str) =>
  (str || "")
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_|_$/g, "");

const CustomFields = () => {
  const [activeTab, setActiveTab] = useState("contact");
  const [definitions, setDefinitions] = useState([]);
  const [loading, setLoading] = useState(false);

  // Offcanvas state
  const [editing, setEditing] = useState(null);
  const [formLabel, setFormLabel] = useState("");
  const [formType, setFormType] = useState("text");
  const [formOptions, setFormOptions] = useState("");
  const [saving, setSaving] = useState(false);

  // Drag state
  const dragItem = useRef(null);
  const dragOverItem = useRef(null);

  const fetchDefinitions = useCallback(async () => {
    setLoading(true);
    try {
      const res = await CRMService.getCustomFieldDefinitions(activeTab, true);
      setDefinitions(res?.data || []);
    } catch (err) {
      console.error("Failed to fetch custom field definitions", err);
      setDefinitions([]);
    }
    setLoading(false);
  }, [activeTab]);

  useEffect(() => {
    fetchDefinitions();
  }, [fetchDefinitions]);

  const filteredDefs = definitions;

  const resetForm = () => {
    setEditing(null);
    setFormLabel("");
    setFormType("text");
    setFormOptions("");
  };

  const openAdd = () => {
    resetForm();
    setTimeout(() => UIkit.offcanvas("#offcanvas-custom-field").show(), 10);
  };

  const openEdit = (def) => {
    setEditing(def);
    setFormLabel(def.field_label);
    setFormType(def.field_type);
    setFormOptions(
      def.options_json ? (Array.isArray(def.options_json) ? def.options_json.join("\n") : "") : "",
    );
    setTimeout(() => UIkit.offcanvas("#offcanvas-custom-field").show(), 10);
  };

  const handleSave = async () => {
    if (!formLabel.trim()) {
      UIkit.notification("Field label is required", "warning");
      return;
    }

    setSaving(true);
    const payload = {
      entity_type: activeTab,
      field_label: formLabel.trim(),
      field_key: editing ? editing.field_key : slugify(formLabel),
      field_type: formType,
      options_json:
        formType === "select"
          ? formOptions
              .split("\n")
              .map((s) => s.trim())
              .filter(Boolean)
          : null,
      sort_order: editing ? editing.sort_order : filteredDefs.length,
    };

    try {
      if (editing) {
        const res = await CRMService.updateCustomFieldDefinition(editing.id, payload);
        setDefinitions((prev) =>
          prev.map((d) => (d.id === editing.id ? res.data : d)),
        );
        UIkit.notification("Field updated", "success");
      } else {
        const res = await CRMService.createCustomFieldDefinition(payload);
        setDefinitions((prev) => [...prev, res.data]);
        UIkit.notification("Field created", "success");
      }
      UIkit.offcanvas("#offcanvas-custom-field").hide();
      resetForm();
    } catch (err) {
      const msg = err?.response?.data?.error || "Failed to save field";
      UIkit.notification(msg, "danger");
    }
    setSaving(false);
  };

  const handleToggle = async (def) => {
    try {
      const res = await CRMService.toggleCustomFieldDefinition(def.id, !def.is_active);
      setDefinitions((prev) =>
        prev.map((d) => (d.id === def.id ? res.data : d)),
      );
    } catch (err) {
      UIkit.notification("Failed to update", "danger");
    }
  };

  // ─── Drag reorder ───
  const handleDragStart = (index) => {
    dragItem.current = index;
  };

  const handleDragEnter = (index) => {
    dragOverItem.current = index;
  };

  const handleDragEnd = async () => {
    if (dragItem.current === null || dragOverItem.current === null) return;
    if (dragItem.current === dragOverItem.current) {
      dragItem.current = null;
      dragOverItem.current = null;
      return;
    }

    const reordered = [...filteredDefs];
    const [removed] = reordered.splice(dragItem.current, 1);
    reordered.splice(dragOverItem.current, 0, removed);

    // Optimistic UI update
    const otherDefs = definitions.filter((d) => d.entity_type !== activeTab);
    setDefinitions([...otherDefs, ...reordered]);

    dragItem.current = null;
    dragOverItem.current = null;

    // Persist new order
    try {
      await CRMService.reorderCustomFieldDefinitions(reordered.map((d) => d.id));
    } catch (err) {
      UIkit.notification("Failed to save order", "danger");
      fetchDefinitions();
    }
  };

  const typeLabel = (type) =>
    FIELD_TYPE_OPTIONS.find((o) => o.value === type)?.label || type;

  return (
    <>
      <section id="custom-fields-page">
        <div id="hms-page-title">
          <h1 className="uk-heading-small">Custom Fields</h1>
        </div>

        <div id="hms-main-body">
          {/* Entity type tabs */}
          <div className="cf-tabs">
            {ENTITY_TABS.map((tab) => (
              <button
                key={tab.key}
                className={`cf-tab ${activeTab === tab.key ? "is-active" : ""}`}
                onClick={() => setActiveTab(tab.key)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          {/* Add button */}
          <div className="uk-flex uk-flex-between uk-flex-top">
            <div className="hms-guest-btn">
              <button
                className="uk-button hms-btn hms-red-btn"
                onClick={openAdd}
              >
                <FeatherIcon name="plus" size={14} /> Add Field
              </button>
            </div>
          </div>

          {/* Field list */}
          <div className="uk-card uk-card-small uk-card-default hms-form-card card-without-filters">
            <div className="uk-overflow-auto">
              {loading && <div className="cf-empty">Loading…</div>}
              {!loading && filteredDefs.length === 0 && (
                <div className="cf-empty">
                  <p>No custom fields yet.</p>
                  <button
                    className="uk-button hms-btn hms-red-btn"
                    onClick={openAdd}
                  >
                    Add your first field
                  </button>
                </div>
              )}
              {!loading && filteredDefs.length > 0 && (
                <table className="uk-table uk-table-small uk-table-divider cf-table">
                  <thead>
                    <tr>
                      <th style={{ width: 36 }}></th>
                      <th>Label</th>
                      <th>Type</th>
                      <th>Status</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredDefs.map((def, index) => (
                      <tr
                        key={def.id}
                        className={`cf-row ${!def.is_active ? "cf-disabled" : ""}`}
                        draggable
                        onDragStart={() => handleDragStart(index)}
                        onDragEnter={() => handleDragEnter(index)}
                        onDragEnd={handleDragEnd}
                        onDragOver={(e) => e.preventDefault()}
                      >
                        <td className="cf-drag-handle">
                          <FeatherIcon name="menu" size={14} />
                        </td>
                        <td>{def.field_label}</td>
                <td>{typeLabel(def.field_type)}</td>
                <td>
                  <span className={`cf-status ${def.is_active ? "active" : "inactive"}`}>
                    {def.is_active ? "Active" : "Disabled"}
                  </span>
                </td>
                <td className="cf-actions">
                  <button
                    className="cf-action-btn"
                    onClick={() => openEdit(def)}
                    uk-tooltip="Edit"
                  >
                    <FeatherIcon name="edit-2" size={14} />
                  </button>
                  <button
                    className="cf-action-btn"
                    onClick={() => handleToggle(def)}
                    uk-tooltip={def.is_active ? "Disable" : "Enable"}
                  >
                    <FeatherIcon name={def.is_active ? "eye-off" : "eye"} size={14} />
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
            </div>
          </div>
        </div>
      </section>

      {/* Offcanvas Add/Edit */}
      <div
        id="offcanvas-custom-field"
        className="uk-modal-full hms-modals uk-modal uk-open uk-dark"
        uk-offcanvas="flip: true; overlay: true"
      >
        <div className="uk-offcanvas-bar">
          <div className="hms-modals-main">
            <button
              className="uk-offcanvas-close uk-close-large"
              type="button"
              uk-close="true"
              onClick={resetForm}
            ></button>
            <div className="uk-modal-header">
              <h2 className="uk-modal-title">
                {editing ? "Edit Field" : "Add Field"}
              </h2>
            </div>
            <div className="uk-modal-body" style={{ paddingTop: 16 }}>
              <div className="uk-margin">
                <label className="uk-form-label">Field Label</label>
                <input
                  className="uk-input"
                  type="text"
                  placeholder="e.g. Property Manager"
                  value={formLabel}
                  onChange={(e) => setFormLabel(e.target.value)}
                />
              </div>
              <div className="uk-margin">
                <label className="uk-form-label">Field Type</label>
                <div className="uk-inline uk-width-1-1">
                  <div uk-form-custom="target: > * > span:first-child" className="uk-form-custom uk-width-1-1">
                    <select
                      value={formType}
                      onChange={(e) => setFormType(e.target.value)}
                    >
                      {FIELD_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                    <button className="uk-button uk-button-default uk-width-1-1" type="button" tabIndex="-1">
                      <span>{typeLabel(formType)}</span>
                      <span uk-icon="icon: chevron-down"></span>
                    </button>
                  </div>
                </div>
              </div>
              {formType === "select" && (
                <div className="uk-margin">
                  <label className="uk-form-label">Options (one per line)</label>
                  <textarea
                    className="uk-textarea"
                    rows={4}
                    placeholder={"Option 1\nOption 2\nOption 3"}
                    value={formOptions}
                    onChange={(e) => setFormOptions(e.target.value)}
                  />
                </div>
              )}
              <div className="uk-margin-top uk-flex uk-flex-left" style={{ gap: 8 }}>
                <button
                  className="uk-button hms-btn hms-red-btn"
                  onClick={handleSave}
                  disabled={saving}
                >
                  {saving ? "Saving…" : editing ? "Update" : "Save"}
                </button>
                <button
                  className="uk-button hms-btn hms-white-btn"
                  onClick={() => {
                    UIkit.offcanvas("#offcanvas-custom-field").hide();
                    resetForm();
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CustomFields;
